import React, { useEffect, useState, useReducer, useMemo } from 'react';
import PropTypes from 'prop-types';
import PartnerRelationsListCard from './PartnerRelationsListCard';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  loadDocumentPartnerRelations,
  loadPartnerRelationTypes,
  saveDocumentPartnerRelation,
  externalSearchForPartner,
  loadPartner
} from 'actions/actionCreators/offer/offerPartnerRelationActions';
import useDebounce from 'utils/hooks/useDebounce';
import { reducer, initialState } from './containerReducer';
import {
  shape,
  required,
  items,
  fromModel,
  when,
  Validator,
  Success,
  Failure,
  ValidationError,
  stopOnFirstFailure
} from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { dateIsGreaterThanGivenDate, isValidDateFormat } from 'utils/pure-validation/primitiveValidators';
import { withTranslation } from 'react-i18next';
import { isCNP, isCIF } from '@totalsoft/validations';
import { useDirtyInfo } from '@totalsoft/change-tracking-react';

const getDocumentPartnerRelationsValidator = (masterPartnerId, t) => {
  const clientIdValidator = (masterPartnerId, otherIds) =>
    Validator(function clientid(currentPartnerId) {
      return currentPartnerId === masterPartnerId || otherIds.includes(masterPartnerId)
        ? Success
        : Failure(ValidationError(t('Offer.PartnerRelations.Validations.CurrentClient')));
    });

  const isCnpOrCifValidator = (PartnerLoading, PartnerLoaded) =>
    Validator(function cnpOrCif(currentPartnerUniqueId) {
      return !isCNP(currentPartnerUniqueId) && !isCIF(currentPartnerUniqueId)
        ? Failure(ValidationError(t('Offer.PartnerRelations.Validations.UniqueID')))
        : !PartnerLoading && !PartnerLoaded
          ? Failure(ValidationError(t('Offer.PartnerRelations.Validations.PartnerNotFound')))
          : Success;
    });

  return items(
    fromModel(model =>
      shape({
        directPartnerId: clientIdValidator(masterPartnerId, [model.reversePartnerId]) |> when(Boolean(masterPartnerId)),
        reversePartnerId: clientIdValidator(masterPartnerId, [model.directPartnerId]) |> when(Boolean(masterPartnerId)),
        directPartnerUniqueID: [required, isCnpOrCifValidator(model.directPartnerLoading, model.directPartnerLoaded)] |> stopOnFirstFailure,
        reversePartnerUniqueID:
          [required, isCnpOrCifValidator(model.reversePartnerLoading, model.reversePartnerLoaded)] |> stopOnFirstFailure,
        relationTypeId: required,
        relationStartDate: [required, isValidDateFormat] |> stopOnFirstFailure,
        relationExpireDate:
          [isValidDateFormat, dateIsGreaterThanGivenDate(model.relationStartDate) |> when(Boolean(model.relationStartDate))]
          |> stopOnFirstFailure
      })
    )
  );
};

export const PartnerRelationsContainer = ({ documentId, siteId, partnerId, startsExpanded, t }) => {
  const dispatch = useDispatch();
  const [localState, localDispatch] = useReducer(reducer, initialState);
  const [confirmedContinueWithoutSaving, setConfirmedContinueWithoutSaving] = useState(false);
  const [cardExpanded, setCardExpanded] = useState(startsExpanded);
  const [filteredDocumentPartnerRelations, setFilteredDocumentPartnerRelations] = useState(null);
  const rowsPerPageOptions = [1, 5, 10, 20];
  const defaultPagination = { page: 1, pageSize: 5, totalCount: 0 };
  const [partnerRelationsFilter, setPartnerRelationsFilter] = useState({
    fullTextFilter: '',
    orderByColumnName: 'lastUpdateDate',
    orderByDescending: false
  });
  const [pagination, setPagination] = useState(defaultPagination);
  const debounceFilterValue = useDebounce(partnerRelationsFilter, 250);
  const [dirtyInfo, setDirtyInfo] = useDirtyInfo();

  const masterPartnerId = useSelector(
    state => (state.entities.offerPartners[partnerId] ? state.entities.offerPartners[partnerId].masterPartnerId : undefined),
    shallowEqual
  );

  const documentPartnerRelationsValidator = useMemo(() => getDocumentPartnerRelationsValidator(masterPartnerId, t), [masterPartnerId, t]);

  const [documentPartnerRelationsValidation, validateDocumentPartnerRelations] = useDirtyFieldValidation(
    documentPartnerRelationsValidator,
    dirtyInfo
  );

  const userProfile = useSelector(
    state => (state.oidc.user && state.oidc.user.profile ? state.oidc.user.profile : undefined),
    shallowEqual
  );

  const { documentPartnerRelations, partnerRelationTypes, partnerRelationTypesIsLoaded, isLoaded } = useSelector(
    state => state.currentOffer.documentPartnerRelations,
    shallowEqual
  );

  useEffect(() => {
    if (!isLoaded) return;
    if (!partnerId) {
      setFilteredDocumentPartnerRelations(
        orderPartnerRelations(
          documentPartnerRelations.map(x => {
            return { ...x, isExpanded: false };
          })
        )
      );
      return;
    }
    if (!masterPartnerId) return;
    setFilteredDocumentPartnerRelations(
      orderPartnerRelations(
        documentPartnerRelations
          .filter(x => x.directPartnerId === masterPartnerId || x.reversePartnerId === masterPartnerId)
          .map(x => {
            return { ...x, isExpanded: false };
          })
      )
    );
  }, [masterPartnerId, isLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!documentId || !cardExpanded || isLoaded) return;
    dispatch(loadDocumentPartnerRelations(documentId, siteId));
    dispatch(loadPartnerRelationTypes());
  }, [cardExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (filteredDocumentPartnerRelations !== null) {
      setPagination(prevPagination => ({
        ...prevPagination,
        totalCount: filteredDocumentPartnerRelations.length
      }));
      localDispatch({
        type: 'OnInitState',
        documentPartnerRelations: getItemsPerPage(
          orderPartnerRelations(filteredDocumentPartnerRelations),
          pagination.pageSize,
          pagination.page,
          partnerRelationsFilter
        )
      });
    }
  }, [filteredDocumentPartnerRelations]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    validateDocumentPartnerRelations(localState.documentPartnerRelations, dirtyInfo);
  }, [localState.documentPartnerRelations, validateDocumentPartnerRelations, dirtyInfo]);

  useEffect(() => {
    if (!filteredDocumentPartnerRelations) return;
    if (isLoaded) {
      updatePageSize(orderPartnerRelations(filteredDocumentPartnerRelations), pagination, partnerRelationsFilter);
    }
  }, [debounceFilterValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPropertyChange = (index, prop1, prop2) => (value1, value2) => {
    localDispatch({ type: 'OnPropertyChange', property: prop1, propertyValue: value1, index: index });
    setDirtyInfo(`${index}.${prop1}`);
    if (prop2) {
      localDispatch({ type: 'OnPropertyChange', property: prop2, propertyValue: value2, index: index });
      setDirtyInfo(`${index}.${prop2}`);
    }

    if ((prop1 === 'directPartnerUniqueID' || prop1 === 'reversePartnerUniqueID') && isCIF(value1) && value1 !== '') {
      const isDirectPartner = prop1 === 'directPartnerUniqueID';
      localDispatch({ type: 'BeforePartnerLoad', index, isDirectPartner: isDirectPartner });
      dispatch(externalSearchForPartner(value1))
        .then(x => {
          localDispatch({
            type: 'OnPartnerLoaded',
            isDirectPartner: isDirectPartner,
            property: prop1,
            data: x.eventBody,
            index: index
          });
        })
        .catch(() => localDispatch({ type: 'OnPartnerLoadFailed', index, isDirectPartner: isDirectPartner }));
    }
    if ((prop1 === 'directPartnerUniqueID' || prop1 === 'reversePartnerUniqueID') && isCNP(value1) && value1 !== '') {
      const isDirectPartner = prop1 === 'directPartnerUniqueID';
      localDispatch({ type: 'BeforePartnerLoad', index, isDirectPartner: isDirectPartner });
      dispatch(loadPartner(value1))
        .then(x => {
          if (x.partnerId === 0) return Promise.reject('Partner not found');
          localDispatch({ type: 'OnPartnerLoaded', isDirectPartner: isDirectPartner, data: x, index: index });
        })
        .catch(() => localDispatch({ type: 'OnPartnerLoadFailed', index, isDirectPartner: isDirectPartner }));
    }
  };

  const onAdd = () => {
    localDispatch({
      type: 'OnAddNewDocumentPartnerRelation',
      documentId: documentId,
      siteId: siteId,
      userProfile: userProfile
    });
  };

  const onSaveCallBack = index => partnerRelationId => {
    if (!partnerRelationId) localDispatch({ type: 'OnPartnerRelationSaveFailed', index });
    else
      localDispatch({
        type: 'OnDocumentPartnerRelationSaved',
        index,
        partnerRelationId
      });
  };

  const onSave = index => {
    if (!validateDocumentPartnerRelations(localState.documentPartnerRelations)) return;
    localDispatch({ type: 'BeforeSavingDocumentPartnerRelation', index });
    dispatch(
      saveDocumentPartnerRelation(
        localState.documentPartnerRelations[index].partnerRelationId,
        localState.documentPartnerRelations[index].documentId,
        localState.documentPartnerRelations[index].siteId,
        localState.documentPartnerRelations[index],
        onSaveCallBack(index)
      )
    );
  };
  const confirmedLosingChanges = () => {
    if (localState.documentPartnerRelations.some(x => x.changed) && !confirmedContinueWithoutSaving)
      if (window.confirm(t('LeavingWithoutSaving'))) {
        setConfirmedContinueWithoutSaving(true);
        return true;
      } else return false;
    return true;
  };
  const onChangeFilterValue = (name, value) => {
    if (!confirmedLosingChanges()) return;
    setPartnerRelationsFilter({ ...partnerRelationsFilter, [name]: value });
  };

  const orderPartnerRelations = partnerRelations => {
    const property = partnerRelationsFilter.orderByColumnName;
    const descending = partnerRelationsFilter.orderByDescending ? -1 : 1;
    return partnerRelations
      .slice()
      .sort((a, b) => (a[property] === b[property] ? 0 : a[property] > b[property] ? descending : -1 * descending));
  };

  const filterPartnerRelationsByValue = (partnerRelations, filter) => {
    const result = partnerRelations.filter(o =>
      Object.keys(o).some(k => {
        if (o[k] !== null && (k === 'reversePartnerName' || k === 'directPartnerName' || k === 'directRelation'))
          return o[k].toString().toLowerCase().includes(filter.toString().toLowerCase());
        return null;
      })
    );
    return result;
  };

  const getItemsPerPage = (partnerRelations, pageSize, page, filter) => {
    let current = (page - 1) * pageSize;
    const relationsOnPage = partnerRelations.slice(current, current + pageSize);
    let result;
    if (filter.fullTextFilter !== '') {
      result = filterPartnerRelationsByValue(partnerRelations, filter.fullTextFilter);
      setPagination({
        ...defaultPagination,
        totalCount: result.length
      });
    } else {
      result = relationsOnPage;
      setPagination({
        ...defaultPagination,
        totalCount: partnerRelations.length,
        pageSize: pageSize,
        page: page
      });
    }
    return result;
  };

  const updatePageSize = (partnerRelations, pagination, filter) => {
    if (!isLoaded) return;
    if (!confirmedLosingChanges()) return;
    localDispatch({
      type: 'OnInitState',
      documentPartnerRelations: getItemsPerPage(partnerRelations, pagination.pageSize, pagination.page, filter)
    });
    if (confirmedContinueWithoutSaving) setConfirmedContinueWithoutSaving(false);
  };

  const onExpand = (_, expanded) => {
    setCardExpanded(!expanded);
  };

  const onChangePage = page => {
    if (!confirmedLosingChanges()) return;
    const newPage = ++page;
    setPagination({ ...pagination, page: newPage });
    updatePageSize(filteredDocumentPartnerRelations, { ...pagination, page: newPage }, partnerRelationsFilter);
  };

  const onChangeRowsPerPage = size => {
    if (!confirmedLosingChanges()) return;
    setPagination({ ...pagination, pageSize: size, page: defaultPagination.page });
    updatePageSize(
      filteredDocumentPartnerRelations,
      { ...pagination, pageSize: size, page: defaultPagination.page },
      partnerRelationsFilter
    );
  };

  const onPaginationAndFiltersRefresh = () => {
    if (!confirmedLosingChanges()) return;
    updatePageSize(
      filteredDocumentPartnerRelations,
      { ...pagination, pageSize: defaultPagination.pageSize },
      {
        fullTextFilter: '',
        orderByColumnName: 'LastUpdateDate',
        orderByDescending: false
      }
    );
    setPagination({ ...defaultPagination, totalCount: filteredDocumentPartnerRelations.length });
  };

  return (
    <PartnerRelationsListCard
      documentPartnerRelations={localState.documentPartnerRelations}
      partnerRelationTypes={partnerRelationTypes}
      onChangeFilterValue={onChangeFilterValue}
      dispatch={localDispatch}
      cardExpanded={cardExpanded}
      onExpand={onExpand}
      loaded={isLoaded && partnerRelationTypesIsLoaded}
      partnerRelationsFilter={partnerRelationsFilter}
      pagination={pagination}
      onRefresh={onPaginationAndFiltersRefresh}
      rowsPerPageOptions={rowsPerPageOptions}
      onChangePage={onChangePage}
      onChangeRowsPerPage={onChangeRowsPerPage}
      onPropertyChange={onPropertyChange}
      onSave={onSave}
      onAdd={onAdd}
      validation={documentPartnerRelationsValidation}
    />
  );
};

PartnerRelationsContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  partnerId: PropTypes.number,
  startsExpanded: PropTypes.bool,
  t: PropTypes.func.isRequired
};

export default withTranslation()(PartnerRelationsContainer);
